import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useDownloadFlowStore } from '@/stores/useDownloadFlowStore';
import { ReviewService } from '@/lib/reviewService';
import type { Sequence } from '@/types/sequence';

// Define the type for purchase details response
interface PurchaseDetailsResponse {
  id: string;
  created_at: string;
  status: string;
}

interface DownloadToken {
  downloadUrl: string;
  token: string;
  expiresAt: string;
  attemptsAllowed: number;
}

export const useSequencePurchase = (sequenceId: string | undefined) => {
  const [isPurchaseProcessing, setIsPurchaseProcessing] = useState(false);
  const [isPurchased, setIsPurchased] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [checkingOwnership, setCheckingOwnership] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const downloadStore = useDownloadFlowStore();

  // Check ownership and purchase status when sequence or user changes
  useEffect(() => {
    const checkOwnershipAndPurchase = async () => {
      if (!sequenceId || !user) {
        setCheckingOwnership(false);
        return;
      }

      setCheckingOwnership(true);
      try {
        // Get sequence details to check ownership
        const { data: sequence, error: sequenceError } = await supabase
          .from('sequences')
          .select('user_id, price')
          .eq('id', sequenceId)
          .single();

        if (sequenceError) throw sequenceError;

        const isOwnerCheck = sequence?.user_id === user.id;
        setIsOwner(isOwnerCheck);

        // If not owner, check if purchased
        if (!isOwnerCheck) {
          const hasPurchased = await ReviewService.hasUserPurchased(sequenceId, user.id);
          setIsPurchased(hasPurchased);
        } else {
          setIsPurchased(false); // Owner doesn't need to purchase
        }
      } catch (error) {
        console.error('Error checking ownership/purchase status:', error);
      } finally {
        setCheckingOwnership(false);
      }
    };

    checkOwnershipAndPurchase();
  }, [sequenceId, user]);

  const handlePurchase = async (sequence: Sequence) => {
    if (!sequence) return;
    
    if (!user) {
      toast.error("Authentication required", {
        description: "Please sign in to purchase this sequence",
      });
      navigate('/auth');
      return;
    }
    
    setIsPurchaseProcessing(true);
    
    try {
      // Create a Stripe checkout session
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          sequenceId: sequence.id,
          userId: user.id,
          price: sequence.price,
          title: sequence.title,
          sellerUserId: sequence.creator.id,
          songTitle: sequence.song?.title || sequence.title,
          songArtist: sequence.song?.artist || 'Unknown Artist',
          callbackUrl: `${window.location.origin}/purchase-success/${sequence.id}`
        }
      });
      
      if (error) throw error;
      
      // Redirect to Stripe Checkout
      window.location.href = data.url;
      
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error("Purchase failed", {
        description: "There was an error processing your purchase",
      });
    } finally {
      setIsPurchaseProcessing(false);
    }
  };
  
  const handleDownload = async (sequence: Sequence) => {
    if (!sequence || !user) return;
    
    // Allow download if: user owns it, has purchased it, OR it's a free sequence
    const isFreeSequence = sequence.price === 0;
    
    if (!isOwner && !isPurchased && !isFreeSequence) {
      toast.error("Access denied", {
        description: "You need to purchase this sequence to download it",
      });
      return;
    }

    setIsDownloading(true);
    downloadStore.setGeneratingUrl(true);
    downloadStore.setDownloadError(null);
    
    try {
      // Start download session in store
      downloadStore.startDownloadSession({
        sequenceId: sequence.id,
        sequenceTitle: sequence.title,
        sequenceArtist: sequence.song?.artist || 'Unknown Artist',
        isOwner,
        isFree: sequence.price === 0,
        price: sequence.price,
        userId: user.id,
        // For now, we'll check if this is a charity sequence by checking the charity field
        // In the future, this should come from proper database schema
        isCharity: Boolean((sequence as any).charity || (sequence as any).is_charity),
        charityName: (sequence as any).charity?.name || (sequence as any).charity_name,
      });

      // Get current auth session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No valid session found');
      }

      // Find purchase ID if this is a purchased sequence
      let purchaseId: string | undefined;
      if (!isOwner) {
        const { data: purchase } = await supabase
          .from('purchases')
          .select('id')
          .eq('sequence_id', sequence.id)
          .eq('user_id', user.id)
          .eq('status', 'completed')
          .single();
        
        purchaseId = purchase?.id;
      }

      downloadStore.setDownloading(true, 25);

      // For now, create a simple direct download using Supabase storage
      // This is a temporary solution until the backend server is properly configured
      
      console.log('Attempting to download sequence:', { 
        id: sequence.id, 
        title: sequence.title,
        file_url: sequence.file_url || 'No file URL available'
      });

      // Check if sequence has a file URL
      if (!sequence.file_url) {
        // Try to get the file URL from the database
        const { data: dbSequence, error: dbError } = await supabase
          .from('sequences')
          .select('file_url')
          .eq('id', sequence.id)
          .single();
        
        if (dbError || !dbSequence?.file_url) {
          throw new Error('Sequence file not found. Please contact support.');
        }
        
        sequence.file_url = dbSequence.file_url;
      }

      // Use the file URL directly (it's already a complete URL from Supabase storage)
      const downloadUrl = sequence.file_url;

      const tokenData: DownloadToken = {
        downloadUrl: downloadUrl,
        token: 'temp-token',
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours from now
        attemptsAllowed: 5,
      };
      downloadStore.setDownloadUrl(tokenData.downloadUrl, tokenData.token);
      downloadStore.setDownloading(true, 75);

      // Create download link and trigger download
      const link = document.createElement('a');
      link.href = tokenData.downloadUrl;
      link.download = `${sequence.title.replace(/[^a-zA-Z0-9\s]/g, '_')}.zip`;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      downloadStore.setDownloading(true, 100);
      
      toast.success("Download started", {
        description: `Your ${isOwner ? 'sequence' : isPurchased ? 're-download' : 'purchased sequence'} is being downloaded`
      });

      // Complete download progress after a short delay
      setTimeout(() => {
        downloadStore.resetDownloadProgress();
        
        // Show appropriate modals based on sequence type and user preferences
        if (!isOwner) {
          // Check if should show donation modal for charity sequences
          if (downloadStore.shouldShowDonationModal()) {
            downloadStore.showDonationModal();
          } else if (downloadStore.shouldShowAddedToDisplayModal()) {
            downloadStore.showAddedToDisplayModal();
          }
        }
      }, 1000);

    } catch (error) {
      console.error('Download error:', error);
      downloadStore.setDownloadError(error instanceof Error ? error.message : 'Download failed');
      toast.error("Download failed", {
        description: error instanceof Error ? error.message : "There was an error starting your download"
      });
    } finally {
      setIsDownloading(false);
      downloadStore.setGeneratingUrl(false);
    }
  };

  // Get appropriate button text based on ownership/purchase status
  const getDownloadButtonText = () => {
    if (checkingOwnership) return 'Checking...';
    if (isOwner) return 'Download';
    if (isPurchased) return 'Re-download';
    return 'Download';
  };

  // Check if user can download (owns, has purchased, or it's free)
  // Note: We need the sequence data to check if it's free, so we'll compute this in the component
  const canDownload = isOwner || isPurchased;

  return {
    isPurchaseProcessing,
    isPurchased,
    isOwner,
    isDownloading,
    checkingOwnership,
    canDownload,
    handlePurchase,
    handleDownload,
    getDownloadButtonText,
  };
};
